import type { PositionSourceKindInput } from "../types/yaasPositions";
import { SolanaAddress } from "../utils/address";

import type { SquadsMultisigInfo, SquadsMultisigStatus } from "./squadsMultisig";

export type PositionSourceDetection =
    | { status: "empty"; sourceKind: null }
    | { status: "invalid"; sourceKind: null }
    | { status: "loading"; sourceKind: null }
    | { status: "eoa"; sourceKind: "eoa"; address: string }
    | {
          status: "multisig-squads";
          sourceKind: "multisig-squads";
          multisig: SquadsMultisigInfo;
      }
    | { status: "unsupported"; sourceKind: null }
    | { status: "unsupported-version"; sourceKind: null; version: string }
    | { status: "lookup-failed"; sourceKind: null; error: string };

/**
 * Fold the pasted input and the Squads preflight state into a single source
 * classification, shared by both consoles' add-position forms: a resolved
 * multisig (pasted as either the multisig PDA or its vault) wins, an
 * on-curve non-Squads address is a plain wallet, and an off-curve
 * non-Squads address is some other program's PDA we can't pay.
 */
export function detectPositionSource(input: string, multisigStatus: SquadsMultisigStatus): PositionSourceDetection {
    const address = input.trim();
    if (!address) return { status: "empty", sourceKind: null };
    if (!SolanaAddress.isShape(address)) {
        return { status: "invalid", sourceKind: null };
    }

    switch (multisigStatus.status) {
        case "idle":
        case "loading":
            return { status: "loading", sourceKind: null };
        case "invalid":
            return { status: "invalid", sourceKind: null };
        case "success":
            return {
                status: "multisig-squads",
                sourceKind: "multisig-squads",
                multisig: multisigStatus.multisig
            };
        case "not-found":
            return SolanaAddress.isOnCurve(address)
                ? { status: "eoa", sourceKind: "eoa", address }
                : { status: "unsupported", sourceKind: null };
        case "vault-unknown-to-system":
            return { status: "unsupported", sourceKind: null };
        case "unsupported-version":
            return {
                status: "unsupported-version",
                sourceKind: null,
                version: multisigStatus.version
            };
        case "lookup-failed":
            return {
                status: "lookup-failed",
                sourceKind: null,
                error: multisigStatus.error
            };
    }
}

export function detectionSourceKind(detection: PositionSourceDetection): PositionSourceKindInput | null {
    return detection.sourceKind;
}
